
import type { CombatResolution, FlyLaunch } from "../client/combat";
import { weaponVnumWorn } from "../wardrobe";
import {
  SkillProjectiles,
  indexedFlyFor,
  launchPoint,
  skillFlyFor,
  type Flight,
  type Landing,
  type Point,
} from "./projectiles";
import {
  AFFECT_LOOP_EFFECTS,
  affectLoopEffectFor,
  DEFAULT_REFINE_GLOW_FAMILY,
  hasAffect,
  effectKeyOf,
  refineGlowEffectFor,
  type AnchoredEffect,
  type RefineGlowFamily,
} from "./effectsanchor";
import type { RenderFrame } from "./entities";
import { HEADING_STEPS } from "../sim/fixed";

/** Chest height, where a fly leaves and lands: the same height `BONE_HEIGHT_MM`
 *  gives a hand bone, since that is what the original's own launch points are. */
export const FLY_CENTRE_MM = 1_110;

/** `CItemData::ITEM_SOCKET_MAX_NUM` (`GameLib/ItemData.h:19`). */
export const ITEM_SOCKET_MAX_NUM = 3;

/** `CItemData::GetRefine()` -- the last decimal digit of the vnum
 *  (`GameLib/ItemData.cpp:421-424`). Zero for an empty hand. */
export function weaponRefine(vnum: number): number {
  return vnum > 0 ? vnum % 10 : 0;
}

export function refineTier(vnum: number, socketCount: number = ITEM_SOCKET_MAX_NUM): number {
  return Math.max(weaponRefine(vnum) + socketCount, ITEM_SOCKET_MAX_NUM) - ITEM_SOCKET_MAX_NUM;
}

export const REFINE_GLOW_BONE = "PART_WEAPON";
export const REFINE_GLOW_BONE_LEFT = "PART_WEAPON_LEFT";
/** The bone Aura's loop hangs on (`playersettingmodule.py:357-358`). */
export const AFFECT_LOOP_BONE = "equip_right_hand";

/** A bone's world pose, as the caller resolved it this frame. */
export interface BonePose {
  readonly xMm: number;
  readonly yMm: number;
  readonly zMm: number;
  readonly headingRad: number;
}

/** Where a named bone of an entity is right now, or null when the caller has
 *  no skeleton for it (not drawn as a mesh, off screen, not yet loaded). */
export type BoneLookup = (entityId: number, bone: string) => BonePose | null;

/** One character the frame has a body for -- the two facts the glows need. */
export interface FxBody {
  readonly id: number;
  /** Worn weapon vnum, 0 for empty-handed. */
  readonly weapon: number;
  /** `EntityDelta.affect_flags`: bit `i` is `CInstanceBase` affect index `i`. */
  readonly affectFlags: number;
  /** `IsTwoHandMode`: drawn in `MODE_TWOHAND_SWORD` (`ActorInstance.cpp:251-257`). */
  readonly twoHand?: boolean;
}

export interface FrameFxInput {
  readonly bodies: readonly FxBody[];
  readonly nowMs: number;
  readonly boneAt: BoneLookup;
  /** The effect manifest's keys, as `EffectRenderer.names` answers them. */
  readonly effects: readonly string[];
  /** Which glow family a worn weapon vnum takes, as
   *  `EffectRenderer.refineGlowFamily` answers it. Omitted answers `sword` for
   *  everything, which is `__GetRefinedEffect`'s `default:` branch. */
  readonly refineFamilyOf?: RefineGlowFamilyLookup;
  /** Where a flight's target is *now*, for homing; null keeps the aim point. */
  readonly targetAt?: (flight: Flight) => Point | null;
}

export interface FrameFxOutput {
  readonly anchored: readonly AnchoredEffect[];
  readonly landings: readonly Landing[];
  readonly flights: readonly Flight[];
}

export type RefineGlowFamilyLookup = (weaponVnum: number) => RefineGlowFamily;

export function anchoredEffectsFor(
  bodies: readonly FxBody[],
  effects: readonly string[],
  boneAt: BoneLookup,
  refineFamilyOf: RefineGlowFamilyLookup = () => DEFAULT_REFINE_GLOW_FAMILY,
): AnchoredEffect[] {
  const known: Record<string, true> = {};
  for (const key of effects) known[key] = true;
  const out: AnchoredEffect[] = [];
  for (const body of bodies) {
    const family = refineFamilyOf(body.weapon);
    const glow = refineGlowEffectFor(refineTier(body.weapon), family);
    if (glow !== null && known[glow] === true) {
      const pose = boneAt(body.id, REFINE_GLOW_BONE);
      if (pose !== null) out.push({ effect: glow, ...pose });
      if (family === "smallsword") {
        const offhand = boneAt(body.id, REFINE_GLOW_BONE_LEFT);
        if (offhand !== null) out.push({ effect: glow, ...offhand });
      }
    }
    for (const index of AFFECT_LOOP_EFFECTS.keys()) {
      if (!hasAffect(body.affectFlags, index)) continue;
      const loop = affectLoopEffectFor(index, known, body.twoHand === true);
      if (loop === null) continue;
      const pose = boneAt(body.id, AFFECT_LOOP_BONE);
      if (pose !== null) out.push({ effect: loop, ...pose });
    }
  }
  return out;
}

/** The manifest key a flight script's `BombEffect` resolves to, or null when
 *  this pack has not imported it -- `EffectRenderer.spawn` draws nothing for
 *  an unknown key, so the landing is still collected, only not painted. */
export function bombEffectKey(bombEffect: string | null, effects: readonly string[]): string | null {
  if (bombEffect === null) return null;
  const key = effectKeyOf(bombEffect);
  return effects.includes(key) ? key : null;
}

/** The blow, as `Connection` hands it to `onCombat`: `CombatEvent` with the
 *  skill vnum lane PN appended to the wire (0 for an ordinary swing). */
export type Blow = CombatResolution;

export class FrameFx {
  readonly projectiles: SkillProjectiles;

  /** `random` is the draw a spreading fly's cone takes. */
  constructor(random: () => number = Math.random) {
    this.projectiles = new SkillProjectiles(random);
  }

  launch(blow: Blow, caster: Point | null, target: Point | null, nowMs: number): Flight | null {
    const vnum = blow.skill ?? 0;
    if (vnum === 0 || caster === null || target === null) return null;
    const kind = skillFlyFor(vnum);
    if (kind === null) return null;
    const key = `${blow.attackerId}:${blow.targetId}:${vnum}:${nowMs}`;
    return this.projectiles.launch(key, vnum, launchPoint(kind, caster), target, nowMs);
  }

  /** One frame: step the flights, drain the landings, compute the glows. */
  frame(input: FrameFxInput): FrameFxOutput {
    this.projectiles.advance(input.nowMs, input.targetAt);
    return {
      anchored: anchoredEffectsFor(
        input.bodies,
        input.effects,
        input.boneAt,
        input.refineFamilyOf,
      ),
      landings: this.projectiles.collect(),
      flights: this.projectiles.inFlight(),
    };
  }

  /** A new world (`EffectRenderer.clear`'s moment). */
  clear(): void {
    this.projectiles.clear();
  }
}

// ------------------------------------------------------------------ the glue

/** The slice of `SceneRenderer` the frame loop wires the output into. Narrow
 *  so the headless `RecordingRenderer` and a test double both satisfy it. */
export interface FxScene {
  groundAtMm(xMm: number, yMm: number): number;
  /** The world pose of an entity's weapon attach bone as of its last draw, or
   *  null when that entity has no drawn skeleton. Optional: a backend with no
   *  skeletons (Canvas2D fallback, the recording renderer) glows nothing. */
  weaponAnchor?(entityId: number): BonePose | null;
  /** The off-hand copy of `weaponAnchor`, for the dagger's second glow --
   *  see `REFINE_GLOW_BONE_LEFT`. Optional for the same reason. */
  weaponAnchorLeft?(entityId: number): BonePose | null;
  /** The combo clip a weapon vnum swings, `attackTwoHand` for a two-handed
   *  one. Optional: without it every body is one-handed. */
  weaponAttackClip?(vnum: number): string;
  effects: {
    readonly names: readonly string[];
    setAnchoredEffects(effects: readonly AnchoredEffect[]): void;
    spawn(key: string, options: { xMm: number; yMm: number; zMm: number; headingRad?: number }): void;
    /** The live flights to draw this frame. Optional: a backend with no
     *  instance buffer draws no streaks and everything else still works. */
    setFlights?(flights: readonly Flight[]): void;
    /** Which glow family a worn weapon vnum takes. Optional for the same
     *  reason: a renderer with no manifest answers the `sword` default. */
    refineGlowFamily?(weaponVnum: number): RefineGlowFamily;
  };
}

/** Whether a body holding `weapon` is in `MODE_TWOHAND_SWORD`: the mode
 *  `SetMotionMode` picks for a two-handed weapon on foot, a rider being in
 *  `MODE_HORSE_TWOHAND_SWORD` instead (`InstanceBase.cpp:2896-2923`). */
export function inTwoHandMode(
  weapon: number,
  mounted: boolean,
  attackClipOf: ((vnum: number) => string) | undefined,
): boolean {
  return !mounted && weapon !== 0 && attackClipOf?.(weapon) === "attackTwoHand";
}

export function fxBodiesFromFrame(
  frame: RenderFrame,
  attackClipOf?: (vnum: number) => string,
): FxBody[] {
  const weapon = weaponVnumWorn(frame.playerWorn);
  const bodies: FxBody[] = [
    {
      id: frame.playerId,
      weapon,
      affectFlags: frame.playerAffectFlags ?? 0,
      twoHand: inTwoHandMode(weapon, (frame.playerMount ?? 0) !== 0, attackClipOf),
    },
  ];
  for (const other of frame.others) {
    if (other.weapon === 0 && other.affectFlags === 0) continue;
    bodies.push({
      id: other.id,
      weapon: other.weapon,
      affectFlags: other.affectFlags,
      twoHand: inTwoHandMode(other.weapon, other.mount !== 0, attackClipOf),
    });
  }
  return bodies;
}

export function applyFrameFx(
  fx: FrameFx,
  frame: RenderFrame,
  scene: FxScene,
): FrameFxOutput {
  const positions = new Map<number, Point>();
  positions.set(frame.playerId, {
    xMm: frame.player.xMm,
    yMm: frame.player.yMm,
    zMm: scene.groundAtMm(frame.player.xMm, frame.player.yMm),
  });
  for (const other of frame.others) {
    positions.set(other.id, {
      xMm: other.xMm,
      yMm: other.yMm,
      zMm: scene.groundAtMm(other.xMm, other.yMm),
    });
  }
  const boneAt: BoneLookup = (entityId, bone) =>
    (bone === REFINE_GLOW_BONE_LEFT
      ? scene.weaponAnchorLeft?.(entityId)
      : scene.weaponAnchor?.(entityId)) ?? null;
  const out = fx.frame({
    bodies: fxBodiesFromFrame(frame, scene.weaponAttackClip?.bind(scene)),
    nowMs: frame.nowMs,
    boneAt,
    effects: scene.effects.names,
    refineFamilyOf: (vnum) =>
      scene.effects.refineGlowFamily?.(vnum) ?? DEFAULT_REFINE_GLOW_FAMILY,
    // Homing follows the target's *current* centre
    // (`FlyingInstance.cpp:351-356`, `ActorInstanceFly.cpp:4-19`): the live
    // feet on the ground, at the height the flight was aimed at.
    targetAt: (flight) => {
      const at = positions.get(flightTargetId(flight));
      return at === undefined ? null : { xMm: at.xMm, yMm: at.yMm, zMm: flight.to.zMm };
    },
  });
  for (const landing of out.landings) {
    const key = bombEffectKey(landing.bombEffect, scene.effects.names);
    if (key === null) continue;
    scene.effects.spawn(key, landing.at);
  }
  scene.effects.setAnchoredEffects(out.anchored);
  // What is still in the air, so the layer draws each flight's own `.mse`
  // sprite along the segment it swept -- the original's `AttachData` effect,
  // moved with the flying object every step (`FlyingInstance.cpp:236-273`).
  scene.effects.setFlights?.(out.flights);
  return out;
}

/**
 * `session.onCombat`'s half: the blow arrives with the skill vnum, and the
 * caster's and target's positions are looked up by whoever holds them.
 */
export function launchFromBlow(
  fx: FrameFx,
  blow: Blow,
  positionOf: (entityId: number) => Point | null,
  nowMs: number,
): Flight | null {
  const vnum = blow.skill ?? 0;
  const kind = skillFlyFor(vnum);
  if (kind === null) return null;
  const caster = positionOf(blow.attackerId);
  const target = positionOf(blow.targetId);
  if (caster === null || target === null) return null;
  // Aimed at the target's centre (`ActorInstanceFly.cpp:4-19`), which stands
  // as high above his feet as the launch bone does above the caster's, so a
  // flight between two standing characters is level, as the original's is.
  const height = launchPoint(kind, caster).zMm - caster.zMm;
  const centre = { xMm: target.xMm, yMm: target.yMm, zMm: target.zMm + height };
  return fx.launch(blow, caster, centre, nowMs);
}

/** `RecvCreateFlyPacket`'s half (`PythonNetworkStreamPhaseGame.cpp:2640-2657`):
 *  the frame names a fly type and two VIDs, and whoever holds the entities
 *  resolves where they are. Nothing flies when either end is off this client's
 *  screen -- the original returns early on the same missing-instance test
 *  (`:2649-2651`).
 *
 *  The mote leaves and lands at chest height rather than at the feet: the
 *  original's `CreateIndexedFly` takes both ends from the characters'
 *  `GraphicThingInstance`s and `CFlyTarget` aims at the target's centre
 *  (`ActorInstanceFly.cpp:4-19`).
 */
export function launchIndexedFly(
  fx: FrameFx,
  launch: FlyLaunch,
  positionOf: (entityId: number) => Point | null,
  nowMs: number,
  headingOf: (entityId: number) => number | null = () => null,
  random: () => number = Math.random,
): Flight | null {
  const kind = indexedFlyFor(launch.fly);
  if (kind === null) return null;
  const from = positionOf(launch.fromId);
  const to = positionOf(launch.toId);
  if (from === null || to === null) return null;
  if (kind.launch === "fireCracker") {
    // `INDEX_FLY_TYPE_FIRE_CRACKER` (`FlyingObjectManager.cpp:190-204`): the
    // second actor is ignored. `GetRotation() - 90` is the thrower's facing,
    // turned by up to thirty degrees either way; the shell leaves 200 units
    // over his feet for a point 2,000 to 5,000 units out and 1,000 to 2,500
    // up, and it aims at that point, so its key names no target (-1).
    const frandom = (low: number, high: number) => low + random() * (high - low);
    const facing = ((headingOf(launch.fromId) ?? 0) / HEADING_STEPS) * Math.PI * 2;
    const angle = facing + (frandom(-30, 30) * Math.PI) / 180; // FlyingObjectManager.cpp:193
    const distanceMm = frandom(2000, 5000) * 10; // FlyingObjectManager.cpp:195
    const riseMm = frandom(1000, 2500) * 10; // FlyingObjectManager.cpp:198
    return fx.projectiles.launchIndexed(
      `${launch.fromId}:-1:fly${launch.fly}:${nowMs}`,
      launch.fly,
      { ...from, zMm: from.zMm + 200 * 10 }, // FlyingObjectManager.cpp:201
      {
        xMm: from.xMm + distanceMm * Math.cos(angle),
        yMm: from.yMm + distanceMm * Math.sin(angle),
        zMm: from.zMm + riseMm,
      },
      nowMs,
    );
  }
  // Source first, target second: `flightTargetId` reads the second field, and
  // homing has to find the same entity for a fly as it does for a skill.
  const key = `${launch.fromId}:${launch.toId}:fly${launch.fly}:${nowMs}`;
  return fx.projectiles.launchIndexed(
    key,
    launch.fly,
    { ...from, zMm: from.zMm + FLY_CENTRE_MM },
    { ...to, zMm: to.zMm + FLY_CENTRE_MM },
    nowMs,
  );
}

/** The target an `FrameFx.launch` key names -- see the key's shape there. */
export function flightTargetId(flight: Flight): number {
  return Number(flight.key.split(":")[1]);
}
